import { Component, ViewChild } from '@angular/core';
import { CvCxLoginAssignToolOptions, LoginAssignTool } from './cxone-logins-assign-tool.model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { CVLocalizationService } from '../../services/cv-localization.service';
import { CxoneOrderManagementGridLoaderComponent } from 'src/app/gamification/cv-marketplace-admin/components/cxone-custom-grid-components/cxone-order-management-grid-loader/cxone-order-management-grid-loader.component';
import { UserAdminService } from 'src/app/admin/user/user-admin.service';
import { CxoneCheckboxCellComponent } from '../../ag-grid-components/cxone-checkbox-cell/cxone-checkbox-cell.component';
import { CxoneCheckboxHeaderComponent } from '../../ag-grid-components/cxone-checkbox-header/cxone-checkbox-header.component';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cxone-logins-assign-tool',
    templateUrl: './cxone-logins-assign-tool.component.html',
    styleUrl: './cxone-logins-assign-tool.component.scss',
})
export class CxoneLoginsAssignToolComponent {
    public options: CvCxLoginAssignToolOptions = null;  
    allowClose: boolean = true;
    headerTitle: string = undefined;
    saveLabel: string = undefined;
    cancelLabel: string = undefined;
    gridOptions: GridOptions = null;
    isLoading: boolean = false;
    isSendingAwards: boolean = false;
    logins: LoginAssignTool[] = [];
    filteredLogins: LoginAssignTool[] = [];
    localizationDic: Map<string, string> = new Map();
    selectedCount: number = 0;
    selectedLogins: LoginAssignTool[] = [];
    
    @ViewChild('cxoneLoginsToolGrid') agGrid: AgGridAngular;

    constructor(
        private dialogConfig: DynamicDialogConfig,
        public dialogRef: DynamicDialogRef,
        private localizationService: CVLocalizationService,
        public userAdminService: UserAdminService
    ) {}

    async ngOnInit() {
        if (!this.dialogConfig?.data?.options) {
            this.options = null;
            return;
        }
        this.options = this.dialogConfig.data.options;
        await this.loadInformation();
        this.setupGrid();
    }

    getLocalizationString(key: string, defaultValue: string) {
        if (this.localizationDic) {
          if (this.localizationDic.has(key)) {
            return this.localizationDic.get(key);
          }
        }
        return defaultValue;
    }

    onDismiss() {
        this.dialogRef.close();
    }

    onClose() {
        this.dialogRef.close();
    }

    async submitLogins() {
        this.isSendingAwards = true;
        try {
            await this.options.submitLogins(this.selectedLogins);
            this.dialogRef.close();
        } catch (error) {
            console.error("Error sending awards:", error);
        } finally {
            this.isSendingAwards = false;
        }
    }

    async loadInformation() {
        // Load localization dictionary
        this.localizationDic = await this.localizationService.GetSection("Manual_Awards");

        // Extract options from dialog configuration
        const { headerTitle, saveLabel, cancelLabel } = this.options;
        this.headerTitle = headerTitle;
        this.saveLabel = saveLabel;
        this.cancelLabel = cancelLabel;
    }

    async loadUsers(gridApi: GridReadyEvent<any, any>) {
        this.isLoading = true;
        try {
            const { getLogins } = this.options;

            gridApi.api.showLoadingOverlay();
    
            // Fetch logins
            const loginResponse = await getLogins()
    
            this.logins = loginResponse.data;
    
            this.filteredLogins = [...this.logins];
            
            gridApi.api.setGridOption("rowData", this.filteredLogins);

            if (this.options.selectedLogins && this.options.selectedLogins.length > 0) {
                const selectedLoginNames = this.options.selectedLogins.map(login => login.LoginName);
                
                // Select the rows and update component state
                gridApi.api.forEachNode(node => {
                    if (selectedLoginNames.includes(node.data.LoginName)) {
                        node.setSelected(true);
                    }
                });

                // Update the selected logins array and count
                this.selectedLogins = this.options.selectedLogins;
                this.selectedCount = this.selectedLogins.length;
            }

        } catch (error) {
            console.error("Error loading users:", error);
        } finally {
            this.isLoading = false;
            this.manageNoRowsOverlay();
            requestAnimationFrame(() => gridApi.api.redrawRows());
        }
    }

    recipientCount(): string {
        const count = this.filteredLogins.length;
        const key = count === 1 ? "Count_Recipient" : "Count_Recipients";
        const defaultText = count === 1 ? "Recipient" : "Recipients";
        return `${count} ${this.getLocalizationString(key, defaultText)}`;
    }

    setupGrid() {
        this.gridOptions = {
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns: true,
            suppressContextMenu: true,
            suppressRowClickSelection: true,
            suppressCellFocus: true,
            rowHeight: 36,
            headerHeight: 44,
            suppressPreventDefaultOnMouseWheel: true,
            loadingOverlayComponent: CxoneOrderManagementGridLoaderComponent,
            suppressHorizontalScroll: true,
            enableBrowserTooltips: false,
            rowSelection: "multiple",
            overlayNoRowsTemplate: `<div class="error-container">
                                <svg class="cxoneFavoriteState mb-2" style="width: 165.94px !important;height:92px !important;">
                                    <use href="../App_Themes/CXone/icons/cxone-no-items.svg#cxone-no-items"></use>
                                </svg>
                                <p class="cxone-error-text" style="color:#758994;">${this.getLocalizationString("No_Recipients_Found", "No recipients found")}</p>
                              </div>`,
            columnDefs: [
                {
                    headerComponent: CxoneCheckboxHeaderComponent,
                    cellRenderer: CxoneCheckboxCellComponent,
                    lockPosition: true,
                    headerClass: 'checkbox-header-style',
                    headerName: this.getLocalizationString("Count_Recipient", "Recipient"),
                    field: "LoginName",
                    filter: "agTextColumnFilter",
                },
                {
                    headerName: this.getLocalizationString("Team", "Team"),
                    field: "ParentGroupName",
                }
            ],
            defaultColDef: {
                resizable: false,
                suppressMenu: true,
            },
            rowData: this.filteredLogins,
            getRowStyle: params => {
                return { background: params.node.rowIndex % 2 === 0 ? '#FFF' : '#F5F8FA' };
            },
            onGridReady: async (params) => {
                if (this.options?.hideColumns) {
                    this.options.hideColumns.forEach(colId => {
                        params.api.setColumnVisible(colId, false);
                    });
                }
                await this.loadUsers(params);
                params.api.sizeColumnsToFit();
            },
            onSelectionChanged: (event) => {
                this.selectedLogins = event.api.getSelectedRows();
                this.selectedCount = this.selectedLogins.length;
            },
        };
    }

    manageNoRowsOverlay() {
        const api = this.agGrid?.api;
        if (!api) return;
    
        api.hideOverlay(); // Hide any existing overlay
    
        if (api.getDisplayedRowCount() === 0) {
            api.showNoRowsOverlay();
        } else {
            api.hideOverlay();
        }
        api.refreshCells();
    }

    userSearchQueryChange(searchQuery: string) {
        // Save the current selected logins
        const currentSelectedLogins = this.selectedLogins;
        const model = this.agGrid.api.getFilterModel();
        
        if (searchQuery && searchQuery.length === 0) {
            this.agGrid.api.setFilterModel(null);
        } else {
            model.LoginName = { type: "startsWith", filter: searchQuery };
            this.agGrid.api.setFilterModel(model);
        }

        if (this.agGrid?.api) {
            this.agGrid.api.setGridOption("rowData", this.filteredLogins);

            // Restore the selected rows
            this.agGrid.api.forEachNode(node => {
                if (currentSelectedLogins.some(login => login.LoginName === node.data.LoginName)) {
                    node.setSelected(true);
                }
            });
            requestAnimationFrame(() =>
                this.agGrid.api.redrawRows()
            );
        }
    }
}
